import { FC, useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Grid,
  Table,
  TableBody,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { useSnackbar } from 'notistack';
import { generateProductReportPDF } from 'src/components/Invoice/ProductReportPDF';
import { generateProductReportCSV } from 'src/utils/csv.utils';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import MainLayout from 'src/pages/private';
import { useGetProductReport } from 'src/services/report.service';
import {
  ReportPageWrapper,
  FilterPaper,
  FilterActionsBoxSmallGap,
  FlexSpacer,
  GenerateButton,
  PdfIconButton,
  CsvIconButton,
  LoadingBox,
  SummaryGrid,
  StatCard,
  StatLabel,
  StatValueNavy,
  StatValueBlue,
  StyledTableContainer,
  HeaderCell,
  DataCell,
  DataCellMedium,
  DataCellBlue,
  EmptyCell,
} from './ReportPage.styled';

const ProductReport: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { loading, reportData, fetchProductReport, error } =
    useGetProductReport();
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().startOf('month')
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const handleGenerateReport = useCallback(() => {
    if (startDate && endDate) {
      fetchProductReport({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
      });
    }
  }, [startDate, endDate, fetchProductReport]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  // Run once on mount — fetchProductReport is stable (useCallback with [])
  useEffect(() => {
    fetchProductReport({
      startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    });
  }, []);

  const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('en-US');
  };

  return (
    <MainLayout title="Product Report">
      <ReportPageWrapper>
        <FilterPaper elevation={0}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} sm={4} md={3}>
                <DatePicker
                  label="Date From"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <DatePicker
                  label="Date To"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FilterActionsBoxSmallGap>
                  <GenerateButton
                    variant="contained"
                    onClick={handleGenerateReport}
                    disabled={loading}
                  >
                    Generate
                  </GenerateButton>
                  <FlexSpacer />
                  <Tooltip title="Export to PDF">
                    <span>
                      <PdfIconButton
                        onClick={() =>
                          reportData &&
                          generateProductReportPDF(reportData, {
                            startDate: startDate?.format('YYYY-MM-DD') || null,
                            endDate: endDate?.format('YYYY-MM-DD') || null,
                          })
                        }
                        disabled={!reportData || loading}
                      >
                        <PictureAsPdfIcon fontSize="small" />
                      </PdfIconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Export to CSV">
                    <span>
                      <CsvIconButton
                        onClick={() =>
                          reportData &&
                          generateProductReportCSV(reportData, {
                            startDate: startDate?.format('YYYY-MM-DD') || null,
                            endDate: endDate?.format('YYYY-MM-DD') || null,
                          })
                        }
                        disabled={!reportData || loading}
                      >
                        <DescriptionIcon fontSize="small" />
                      </CsvIconButton>
                    </span>
                  </Tooltip>
                </FilterActionsBoxSmallGap>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </FilterPaper>

        {loading ? (
          <LoadingBox>
            <CircularProgress />
          </LoadingBox>
        ) : (
          <>
            <SummaryGrid container spacing={3}>
              <Grid item xs={12} sm={6} lg={4}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    TOTAL NET PRODUCTION
                  </StatLabel>
                  <StatValueNavy variant="h4">
                    {formatNumber(reportData?.summary.totalProduction)}
                  </StatValueNavy>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    TOTAL NET SALES
                  </StatLabel>
                  <StatValueBlue variant="h4">
                    {formatNumber(reportData?.summary.totalSales)}
                  </StatValueBlue>
                </StatCard>
              </Grid>
            </SummaryGrid>

            <StyledTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <HeaderCell>PRODUCT NAME</HeaderCell>
                    <HeaderCell align="right">NET PRODUCTION</HeaderCell>
                    <HeaderCell align="right">NET SALES</HeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData?.products.length === 0 ? (
                    <TableRow>
                      <EmptyCell colSpan={3} align="center">
                        <Typography color="var(--subtext)">
                          No products found for the selected criteria.
                        </Typography>
                      </EmptyCell>
                    </TableRow>
                  ) : (
                    reportData?.products.map((product) => {
                      return (
                        <TableRow key={product.id} hover>
                          <DataCell>{product.name}</DataCell>
                          <DataCellMedium align="right">
                            {formatNumber(product.netProduction)}
                          </DataCellMedium>
                          <DataCellBlue align="right">
                            {formatNumber(product.netSales)}
                          </DataCellBlue>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </>
        )}
      </ReportPageWrapper>
    </MainLayout>
  );
};

export default ProductReport;
